import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import Avatar from '../shared/Avatar';
import { VerifiedBadge } from '../ui/Badge';

/**
 * SellerCard
 *
 * Displays seller information on raffle detail page.
 * Security: Only shows public info (displayName, photoURL, rating).
 * Does NOT expose email, phone, or other PII.
 */
export default function SellerCard({
  seller,
  showViewProfile = true,
  className,
}) {
  if (!seller) return null;

  const {
    id,
    displayName,
    photoURL,
    rating,
    reviewCount = 0,
    phoneVerified = false,
    createdAt,
  } = seller;

  // Calculate member since
  const memberSince = createdAt?.toDate
    ? createdAt.toDate().getFullYear()
    : new Date().getFullYear();

  return (
    <div className={cn('bg-midnight-lighter rounded-lg p-4', className)}>
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <Avatar
          src={photoURL}
          name={displayName}
          size="lg"
        />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-cream truncate">
              {displayName}
            </h3>
            {phoneVerified && <VerifiedBadge />}
          </div>

          {/* Rating */}
          {rating ? (
            <div className="flex items-center gap-1 mt-1">
              <StarRating rating={rating} />
              <span className="text-sm text-cream/50">
                ({reviewCount} review{reviewCount !== 1 ? 's' : ''})
              </span>
            </div>
          ) : (
            <p className="text-sm text-cream/50 mt-1">
              New seller
            </p>
          )}

          {/* Member since */}
          <p className="text-xs text-cream/40 mt-1">
            Member since {memberSince}
          </p>
        </div>
      </div>

      {/* View profile link */}
      {showViewProfile && id && (
        <Link
          to={`/seller/${id}`}
          className="block mt-4 text-center text-sm text-primary hover:text-primary-light font-medium transition-colors"
        >
          View Profile
        </Link>
      )}
    </div>
  );
}

/**
 * CompactSellerCard
 *
 * Smaller inline version of seller info.
 * Supports both light and dark backgrounds via the `light` prop.
 */
export function CompactSellerCard({ seller, light = false, className }) {
  if (!seller) return null;

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <Avatar
        src={seller.photoURL}
        name={seller.displayName}
        size="sm"
      />
      <div>
        <div className="flex items-center gap-1">
          <span className={cn(
            'text-sm font-medium',
            light ? 'text-midnight' : 'text-cream'
          )}>
            {seller.displayName}
          </span>
          {seller.phoneVerified && (
            <VerifiedIcon className="w-4 h-4 text-success" />
          )}
        </div>
        {seller.rating && (
          <div className={cn(
            'flex items-center gap-1 text-xs font-mono',
            light ? 'text-midnight/60' : 'text-cream/60'
          )}>
            <StarIcon className="w-3 h-3 text-accent" />
            <span>{seller.rating.toFixed(1)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * StarRating
 *
 * Displays star rating with accent color for filled stars.
 */
function StarRating({ rating, maxStars = 5 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(maxStars)].map((_, i) => (
        <StarIcon
          key={i}
          className={cn(
            'w-4 h-4',
            i < Math.round(rating) ? 'text-accent' : 'text-cream/20'
          )}
          filled={i < Math.round(rating)}
        />
      ))}
      <span className="ml-1 text-sm font-mono font-medium text-cream/70">
        {rating.toFixed(1)}
      </span>
    </div>
  );
}

// Icons
function StarIcon({ className, filled = true }) {
  return (
    <svg
      className={className}
      fill={filled ? 'currentColor' : 'none'}
      viewBox="0 0 20 20"
      stroke="currentColor"
    >
      <path
        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
      />
    </svg>
  );
}

function VerifiedIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}
